/**
 * Small client for the admin API. Responses use the envelope `{ status, message, data }` (D-33);
 * `api()` returns `data` on success and throws `ApiError` with `data.code` on failure.
 * The session cookie travels with every request (`credentials: "include"`), so the API must
 * list the dashboard origin in TRUSTED_ORIGINS when it runs on another origin (D-31).
 */
import type { ResponseStatus } from "@aiyo/shared";
import { apiUrl } from "./config";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type Method = "GET" | "POST" | "PATCH" | "DELETE";

interface EnvelopeBody {
  status?: ResponseStatus;
  message?: string;
  data?: unknown;
}

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

export async function api<T>(method: Method, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      method,
      credentials: "include",
      headers:
        body === undefined
          ? { accept: "application/json" }
          : { "content-type": "application/json", accept: "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "network_error", "Network error");
  }
  const text = await res.text();
  const parsed = (text ? safeJson(text) : null) as EnvelopeBody | null;

  if (!res.ok || parsed?.status === "error") {
    const data = (parsed?.data ?? {}) as { code?: string; details?: unknown };
    // /auth/me decides whether the user is signed in, so its 401 must not trigger the handler (avoids a loop).
    if (res.status === 401 && !path.endsWith("/auth/login") && !path.endsWith("/auth/me")) onUnauthorized?.();
    throw new ApiError(
      res.status,
      data.code ?? "http_error",
      parsed?.message ?? `HTTP ${res.status}`,
      data.details,
    );
  }
  return (parsed?.data ?? null) as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const get = <T>(path: string) => api<T>("GET", path);
export const post = <T>(path: string, body?: unknown) => api<T>("POST", path, body ?? {});
export const patch = <T>(path: string, body: unknown) => api<T>("PATCH", path, body);
export const del = <T>(path: string) => api<T>("DELETE", path);

/** Validation issues from the API as `{ path, message }`. */
export function validationIssues(err: unknown): Array<{ path: string; message: string }> {
  if (!(err instanceof ApiError)) return [];
  const details = err.details as
    | { issues?: Array<{ path?: (string | number)[]; message: string }> | Record<string, unknown> }
    | undefined;
  const issues = details?.issues;
  if (Array.isArray(issues)) {
    return issues.map((i) => ({ path: (i.path ?? []).join("."), message: i.message }));
  }
  if (issues && typeof issues === "object") {
    // z.treeifyError shape, used by credential config validation.
    const out: Array<{ path: string; message: string }> = [];
    const walk = (node: unknown, prefix: string) => {
      if (!node || typeof node !== "object") return;
      const n = node as { errors?: string[]; properties?: Record<string, unknown> };
      for (const message of n.errors ?? []) out.push({ path: prefix, message });
      for (const [k, v] of Object.entries(n.properties ?? {})) walk(v, prefix ? `${prefix}.${k}` : k);
    };
    walk(issues, "");
    return out;
  }
  return [];
}
